import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { agentService } from "../../services/agentService";
import { binService } from "../../services/binService";
import { useAuth } from "../../contexts/AuthContext";
import {
  MagnifyingGlassIcon,
  StarIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { preAuthIntent } from "../../utils/preAuthIntent";
import { readCache, writeCache } from "../../utils/readCache";
import LogoLoader from "../Common/LogoLoader";

const PAGE_SIZE = 12;

const AgentList = ({ hideBecomeAgentCta = false }) => {
   const navigate = useNavigate();
  const cachedAgents = readCache("agents");
  const [agents, setAgents] = useState(() =>
    Array.isArray(cachedAgents) ? cachedAgents : []
  );
  const [filteredAgents, setFilteredAgents] = useState(() =>
    Array.isArray(cachedAgents) ? cachedAgents : []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(() => !Array.isArray(cachedAgents));
  const [loadError, setLoadError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("all");
  const [assigningAgentId, setAssigningAgentId] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const fetchAgents = async () => {
      try {
        const data = await agentService.getAllAgents();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setAgents(list);
        setFilteredAgents(list);
        writeCache("agents", list);
        setLoadError("");
      } catch (error) {
        console.error("Error fetching agents:", error);
        if (!cancelled && !Array.isArray(cachedAgents)) {
          setLoadError("Agents could not be loaded. Check your connection and try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAgents();
    return () => {
      cancelled = true;
    };
  }, [retryToken]);

  useEffect(() => {
    let filtered = agents;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (agent) => {
          const details = agent.agentDetails || {};
          return (
            (details.agent_name || agent.agent_name || agent.username)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            agent.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (details.localGovernmentAddress || agent.localGovernmentAddress || agent.local_government_address)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (details.streetAddress || agent.streetAddress)?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
      );
    }

    // Filter by state
    if (selectedState !== "all") {
      filtered = filtered.filter((agent) => agent.state === selectedState);
    }

    setFilteredAgents(filtered);
  }, [agents, searchTerm, selectedState]);

  useEffect(() => {
    setCurrentPage(1);
  }, [agents, searchTerm, selectedState]);

  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedAgents = useMemo(() => {
    const page = Math.min(currentPage, totalPages);
    const start = (page - 1) * PAGE_SIZE;
    return filteredAgents.slice(start, start + PAGE_SIZE);
  }, [filteredAgents, currentPage, totalPages]);

  // Get unique states for filter
  const states = [
    ...new Set(agents.map((agent) => agent.state).filter(Boolean)),
  ];

  const handleAssignAgent = async (agent) => {
    // Visitors must login before assigning an agent.
    if (!user) {
      preAuthIntent.setPendingAgent(agent._id);
      navigate("/login");
      return;
    }

    const agentName = agent.agentDetails?.agent_name || agent.agent_name || agent.username;

    setAssigningAgentId(agent._id);
    setNotification({ show: false, message: '', type: 'success' });
    
    try {
      await binService.assignAgent(agent._id);
      toast.success(`Agent assigned! ${agentName} will contact you to schedule collection.`);
      setNotification({
        show: true,
        message: `Agent assigned successfully! ${agentName} has been assigned to your bin. They will contact you to schedule collection.`,
        type: 'success'
      });
      setTimeout(() => {
        navigate("/sell");
      }, 2000);
    } catch (error) {
      console.error("Error assigning agent:", error);
      toast.error(error.response?.data?.message || "Could not assign agent.");
      setNotification({
        show: true,
        message: error.response?.data?.message || "Error assigning agent. Please try again.",
        type: 'error'
      });
    } finally {
      setAssigningAgentId(null);
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg max-w-md ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className="ml-4 flex-shrink-0 text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Header — agent registration CTA */}
      {!hideBecomeAgentCta &&
        (user?.role === "agent" ? null : user?.agentRegistrationStatus === "pending" ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
            Your agent registration request is <strong>under review</strong>. We will email you when it has been approved.
          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate("/agent/register")}
            className="flex items-center text-white bg-green-600 hover:bg-green-700 mb-4 px-4 py-2 rounded-md transition-colors font-medium shadow hover:shadow-lg"
          >
            Become a collection agent
          </button>
        ))}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Collection Agents
        </h1>
        <p className="text-gray-600">
          Find a collection agent in your area to validate and collect your
          materials.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search agents by name, state, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPinIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40">
          <LogoLoader />
        </div>
      )}

      {loadError && !loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-700">{loadError}</p>
          <button
            type="button"
            onClick={() => {
              setLoadError("");
              setLoading(true);
              setRetryToken((token) => token + 1);
            }}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md"
          >
            Try again
          </button>
        </div>
      )}

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedAgents.map((agent) => {
          const details = agent.agentDetails || {};
          const agentName =
            details.agent_name || agent.agent_name || agent.username;
          const agentImage = details.agentImage || agent.agentImage;
          const phoneNumber =
            details.phone_number || agent.phone_number || agent.phoneNumber;
          const state = agent.state || details.state;
          const lga =
            details.localGovernmentAddress || agent.localGovernmentAddress;
          const streetAddress = details.streetAddress || agent.streetAddress || '';
          const openHours = details.openHours || agent.openHours;
          const isAssigning = assigningAgentId === agent._id;
          return (
            <div
              key={agent._id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                {/* Agent Image */}
                <div className="flex items-center mb-4">
                  {agentImage ? (
                    <img
                      src={agentImage}
                      alt={agentName}
                      loading="lazy"
                      decoding="async"
                      className="h-16 w-16 rounded-full object-cover mr-4"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                      <span className="text-gray-500 text-sm">No Image</span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {agentName}
                    </h3>
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-sm text-gray-600">
                        {agent.ratings?.toFixed(1) || "No ratings"} (
                        {agent.rating_count || 0})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    <span>
                      {state}, {lga}
                    </span>
                  </div>
                  {streetAddress && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPinIcon className="h-4 w-4 mr-2" />
                      <span>{streetAddress}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    <span>{phoneNumber}</span>
                  </div>
                </div>

                {/* Open Hours */}
                {openHours && (
                  <div className="mb-4">
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      <span className="font-medium">Open Hours</span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {Object.entries(openHours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between">
                          <span className="capitalize">{day}:</span>
                          <span>
                            {hours.open && hours.close
                              ? `${hours.open} - ${hours.close}`
                              : "Not set"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAssignAgent(agent)}
                    disabled={isAssigning}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center"
                  >
                    {isAssigning ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Assigning...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Assign
                      </span>
                    )}
                  </button>
                  <Link
                    to={`/agents/${agent._id}`}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-center flex items-center justify-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAgents.length > PAGE_SIZE && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white rounded-lg shadow px-6 py-4">
          <p className="text-sm text-gray-600">
            Showing {(safePage - 1) * PAGE_SIZE + 1}–
            {Math.min(safePage * PAGE_SIZE, filteredAgents.length)} of {filteredAgents.length} agents
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 px-2">
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && !loadError && filteredAgents.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600 text-lg">
            {searchTerm || selectedState !== "all"
              ? "No agents found matching your criteria."
              : "No agents available at the moment."}
          </p>
          {(searchTerm || selectedState !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedState("all");
              }}
              className="mt-4 text-green-600 hover:text-green-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentList;
